/**
 * Network + identity-bookkeeping layer for the identity overlay (DB slice 2c, identity-overlay
 * rework). No markup is built here — `game-start-identity-overlay.js` owns the overlay's markup
 * and calls into these functions; this module owns the wire calls, the status→error-key mapping,
 * the client-side identity contracts, and the one piece of DOM that carries them: the
 * `data-registered-name` shell attribute read and written below.
 *
 * Identity state: the acting player's registered name lives in `data-registered-name` on the
 * `#skat-app` shell — server-rendered at page load (read-only lookup — GameController) and kept as
 * the single client-side source of truth: a successful register writes the new name back onto the
 * same attribute (`applyRegistered`). Login and logout instead reload the page — the identity
 * switched, so every identity-derived surface (active runs, leaderboard, shell attribute) must
 * re-source, and a reload is the honest full re-sync. `playerName` follows the new identity
 * (registered name on login, cleared on logout) — there is no client-side serde store left to clear
 * (client-serde-retirement, 2026-07-12): the server-side park/relink is the whole identity-switch
 * gate now (AuthController Javadoc).
 *
 * Login/logout also stash the identity-action-confirmation flash token (sessionStorage, not DOM)
 * immediately before reloading, so the confirmation beat survives the reload — see
 * `game-start-identity-confirmation.js`.
 */
import { postApi, getApi } from "../api.js";
import { stashIdentityFlash } from "./game-start-identity-confirmation.js";

export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 71;

/** The acting player's registered display name, or null when anonymous. */
export function getRegisteredName() {
  const name = document.getElementById("skat-app")?.dataset.registeredName;
  return name == null || name === "" ? null : name;
}

/** Post-register: the shell attribute is the source of truth — write it back in place. */
export function applyRegistered(registered, settings) {
  const app = document.getElementById("skat-app");
  if (app != null) app.dataset.registeredName = registered;
  settings.playerName = registered;
  localStorage.setItem("playerName", registered);
}

/**
 * GET /api/name-status?name=X → "AVAILABLE" | "REGISTERED" | "UNAVAILABLE" — the identity overlay's
 * availability precheck. Caller guards staleness (last-request-wins); this is a thin transport.
 */
export async function checkNameStatus(name) {
  const { status } = await getApi(
    `name-status?name=${encodeURIComponent(name)}`,
  );
  return status;
}

/**
 * POST /api/name — the durable half of an anonymous name commit (board-approval design §
 * `POST /api/name`): `game-start-identity-overlay.js#commitAnonymousName` already writes
 * `settings.playerName`/localStorage synchronously; this call is what makes the name survive a
 * server reboot (the anonymous-name-not-persisted-durably gap this endpoint closes). Fire-and-
 * forget by design, same error handling as the module's other submit functions — a failure here
 * just means this attempt didn't durably land; the next session start self-heals it via
 * `resolveAndTouch`, so there's nothing to retry or surface to the player.
 */
export async function persistName(name) {
  try {
    const res = await postApi("name", { name });
    if (!res.ok) {
      console.warn(`[game-start-auth] persistName → ${res.status}`);
    }
  } catch (e) {
    console.warn("[game-start-auth] persistName failed:", e?.message ?? e);
  }
}

async function submitCredentials(endpoint, name, password, onError) {
  try {
    return await postApi(endpoint, { name, password });
  } catch (e) {
    console.warn(`[game-start-auth] ${endpoint} failed:`, e?.message ?? e);
    onError("gameStartAuthErrorGeneric");
    return null;
  }
}

/** Register: on success calls `onSuccess()` (the overlay applies the registered name and closes
 *  itself); on failure maps the status to an error key via `onError(key)`. A 409 fires even after
 *  an AVAILABLE precheck — the race arbiter is server-side (AuthController), this just surfaces it. */
export async function submitRegister(name, password, onSuccess, onError) {
  const res = await submitCredentials("register", name, password, onError);
  if (res == null) return;
  if (res.ok) {
    onSuccess();
    return;
  }
  onError(
    res.status === 409
      ? "gameStartAuthErrorNameUnavailable"
      : res.status === 400
        ? "gameStartAuthErrorNameShape"
        : "gameStartAuthErrorGeneric",
  );
}

/** Login: success reloads (the identity switched, see file header) — nothing left for the overlay
 *  to do, so no onSuccess hook. Failure maps the status to an error key. */
export async function submitLogin(name, password, onError) {
  const res = await submitCredentials("login", name, password, onError);
  if (res == null) return;
  if (res.ok) {
    const body = await res.json();
    localStorage.setItem("playerName", body.name);
    // Survives the reload — game-start-ui.js#renderGameStart consumes it (identity-action-
    // confirmation design's flash token).
    stashIdentityFlash("login");
    window.location.reload();
    return;
  }
  onError(
    res.status === 401
      ? "gameStartAuthErrorLoginFailed"
      : res.status === 429
        ? "gameStartAuthErrorRateLimited"
        : "gameStartAuthErrorGeneric",
  );
}

/** Logout: success reloads, same as login. Failure leaves identity unchanged — warn and let the
 *  overlay surface a generic error rather than pretending a clean slate. */
export async function submitLogout(onError) {
  try {
    const res = await postApi("logout", {});
    if (!res.ok) throw new Error(`logout → ${res.status}`);
    localStorage.removeItem("playerName");
    stashIdentityFlash("logout");
    window.location.reload();
  } catch (e) {
    console.warn("[game-start-auth] logout failed:", e?.message ?? e);
    onError("gameStartAuthErrorGeneric");
  }
}

/** Change password: on success calls `onSuccess()` (the overlay shows the "password changed"
 *  note); nothing about displayed identity changes, so no reload. */
export async function submitChangePassword(newPassword, onSuccess, onError) {
  try {
    const res = await postApi("change-password", { newPassword });
    if (res.ok) {
      onSuccess();
      return;
    }
    onError("gameStartAuthErrorGeneric");
  } catch (e) {
    console.warn("[game-start-auth] change-password failed:", e?.message ?? e);
    onError("gameStartAuthErrorGeneric");
  }
}
